import {
  all,
  call,
  put,
  takeEvery,
  takeLatest,
  select,
} from 'redux-saga/effects';
import qs from 'qs';
import SUCCESS_MESSAGES from '../../common/constants/SuccessMessagesConstants';
import { request } from '../../common/util/RestClient';
import envConfig from '../../config';
import {
  createEmailTemplate,
  deleteEmailTemplate,
  duplicateEmailTemplate,
  fetchAllEmailTemplate,
  fetchEmailTemplateById,
  fetchPartialEmailTemplates,
  updateEmailTemplate,
} from '../routines/emailEditorTemplateRoutine';
import {
  EMAIL_TEMPLATE_GROUP_KEY,
  EMAIL_TEMPLATE_STATUS,
} from '../../common/constants/EmailTemplateConstants';
import { GLOBAL_DEFAULT_PAGE_SIZE } from '../../common/constants/CommonConstants';
import { convertFilterToVfl } from '../../common/util/velarisFilterLanguageUtils';

const BULK_EMAIL_MGT_URL = `${envConfig.APIG_URL}email-template`;

const getEmailTemplateApiHelper = (payload = {}) => {
  const {
    page = 1,
    pageSize = GLOBAL_DEFAULT_PAGE_SIZE,
    query,
    filters,
    templateType,
    myTemplates,
  } = payload || {};

  const params = {
    page: page || 1,
    pageSize: pageSize || GLOBAL_DEFAULT_PAGE_SIZE,
    myTemplates: myTemplates || false,
  };

  if (templateType) {
    if (templateType === EMAIL_TEMPLATE_GROUP_KEY.PUBLIC) {
      params.accessType = EMAIL_TEMPLATE_GROUP_KEY.PUBLIC;
    } else if (templateType === EMAIL_TEMPLATE_GROUP_KEY.PRIVATE) {
      params.accessType = EMAIL_TEMPLATE_GROUP_KEY.PRIVATE;
    }
  }

  if (filters && filters.length > 0) {
    params.filters = convertFilterToVfl(filters);
  }

  if (query && query.length > 0) {
    params.query = query;
  }

  const url = `${BULK_EMAIL_MGT_URL}?${qs.stringify(params)}`;
  return request(url);
};

export function* fetchAllEmailTemplatesSaga({ payload }) {
  const { currentPayload: prevPayload } = yield select((state) => state.tasks);

  const payloadToUse =
    payload && payload.usePrevPayload ? prevPayload : payload;

  try {
    const response = yield call(getEmailTemplateApiHelper, payloadToUse);
    yield put(
      fetchAllEmailTemplate.success({
        response: response.data,
        currentPayload: payloadToUse,
      })
    );
  } catch (error) {
    yield put(fetchAllEmailTemplate.failure({ error }));
  } finally {
    yield put(fetchAllEmailTemplate.fulfill());
  }
}

export function* fetchPartialEmailTemplatesSaga({ payload }) {
  try {
    const response = yield call(
      request,
      `${BULK_EMAIL_MGT_URL}/partial?status=${EMAIL_TEMPLATE_STATUS.ACTIVE}${
        payload?.accessType === EMAIL_TEMPLATE_GROUP_KEY.PUBLIC
          ? '&accessType=public'
          : ''
      }`
    );
    yield put(fetchPartialEmailTemplates.success(response.data));
  } catch (error) {
    yield put(fetchPartialEmailTemplates.failure({ error }));
  } finally {
    yield put(fetchPartialEmailTemplates.fulfill());
  }
}

export function* fetchEmailTemplateByIdSaga({ payload }) {
  try {
    const response = yield call(
      request,
      `${BULK_EMAIL_MGT_URL}/${payload.templateId}`
    );
    yield put(fetchEmailTemplateById.success(response.data));
  } catch (error) {
    yield put(fetchEmailTemplateById.failure({ error, noAlert: true }));
  } finally {
    yield put(fetchEmailTemplateById.fulfill());
  }
}
export function* createEmailTemplateSaga({ payload }) {
  try {
    const { formData, callbackFn } = payload;
    const response = yield call(request, `${BULK_EMAIL_MGT_URL}`, {
      method: 'POST',
      payload: formData,
    });
    yield put(
      createEmailTemplate.success({
        response,
        successMessage: SUCCESS_MESSAGES.CREATE_EMAIL_TEMPLATE,
      })
    );
    if (callbackFn && response?.data?.templateId) {
      callbackFn(response.data.templateId);
    }
  } catch (error) {
    yield put(createEmailTemplate.failure({ error }));
  } finally {
    yield put(createEmailTemplate.fulfill());
  }
}
export function* updateEmailTemplateSaga({ payload }) {
  try {
    const { formData, templateId, scope } = payload;
    const response = yield call(
      request,
      `${BULK_EMAIL_MGT_URL}/${templateId}`,
      {
        method: 'PUT',
        payload: formData,
      }
    );
    yield put(
      updateEmailTemplate.success({
        response,
        successMessage: SUCCESS_MESSAGES.UPDATE_EMAIL_TEMPLATE,
      })
    );
    if (scope === EMAIL_TEMPLATE_STATUS.DRAFT) {
      yield put(fetchEmailTemplateById.trigger({ templateId }));
    }
  } catch (error) {
    yield put(updateEmailTemplate.failure({ error }));
  } finally {
    yield put(updateEmailTemplate.fulfill());
  }
}
export function* deleteEmailTemplateSaga({ payload }) {
  try {
    const { templateId } = payload;
    const response = yield call(
      request,
      `${BULK_EMAIL_MGT_URL}/${templateId}`,
      {
        method: 'DELETE',
      }
    );
    yield put(
      deleteEmailTemplate.success({
        response,
        successMessage: SUCCESS_MESSAGES.DELETE_EMAIL_TEMPLATE,
      })
    );
  } catch (error) {
    yield put(deleteEmailTemplate.failure({ error }));
  } finally {
    yield put(deleteEmailTemplate.fulfill());
  }
}

export function* duplicateEmailTemplateSaga({ payload }) {
  try {
    yield put(fetchEmailTemplateById.trigger(payload));
    yield put(duplicateEmailTemplate.success(payload));
  } catch (error) {
    yield put(duplicateEmailTemplate.failure({ error }));
  } finally {
    yield put(duplicateEmailTemplate.fulfill());
  }
}

export default function* root() {
  yield all([
    takeLatest(fetchAllEmailTemplate.TRIGGER, fetchAllEmailTemplatesSaga),
    takeLatest(
      fetchPartialEmailTemplates.TRIGGER,
      fetchPartialEmailTemplatesSaga
    ),
    takeLatest(fetchEmailTemplateById.TRIGGER, fetchEmailTemplateByIdSaga),
    takeEvery(createEmailTemplate.TRIGGER, createEmailTemplateSaga),
    takeEvery(updateEmailTemplate.TRIGGER, updateEmailTemplateSaga),
    takeEvery(deleteEmailTemplate.TRIGGER, deleteEmailTemplateSaga),
    takeEvery(duplicateEmailTemplate.TRIGGER, duplicateEmailTemplateSaga),
  ]);
}
